import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';

import { useAIDocumentEditorContext } from '../../contexts/AIDocumentEditorContext';
import { useBookingsContext } from '../../contexts/BookingsContext';

import SliderBar from '../../components/SliderBar';
import Panels from './Panels';
import DocumentWorkspace from './DocumentWorkspace';

import { useReportEditorStyles } from './styles';
import { IDelta, IForm, IPromptCard } from '@homeporter/types';
import { ExitToAppOutlined } from '@mui/icons-material';
import CustomButton from '../../components/CustomButton';
import { deepClone } from '../../utils/helpers';
import { useCopilotContext } from '../../contexts/CopilotContext';
import useKnowledgeLibrary from '../../hooks/useKnowledgeLibrary';

interface DocumentDialogProps {
  open: boolean;
  mode: 'new' | 'edit';
  dialogTitle: string;
  onClose: () => void;
  onConfirm: () => void;
  docTitle: string;
  setNewDocTitle: (title: string) => void;
  tempTemplateName: string;
  handleTemplateSelect: (e: any) => void;
  documentTemplates: { template_name: string; title: string }[];
  styles: any;
}

const DocumentDialog = ({
  open,
  mode,
  dialogTitle,
  onClose,
  onConfirm,
  docTitle,
  setNewDocTitle,
  tempTemplateName,
  handleTemplateSelect,
  documentTemplates,
  styles,
}: DocumentDialogProps) => {
  const isEditMode = mode === 'edit';

  return (
    <Dialog
      PaperProps={{ sx: { minWidth: '40rem' } }}
      open={open}
      onClose={onClose}>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent sx={{ pt: '.5rem!important' }}>
        <TextField
          sx={styles.input}
          autoFocus
          fullWidth
          margin="dense"
          label="Document Title"
          type="text"
          variant="outlined"
          value={docTitle}
          onChange={(e) => setNewDocTitle(e.target.value)}
        />
        <FormControl fullWidth sx={{ mt: '2.5rem' }}>
          <InputLabel sx={isEditMode ? styles.inputLabel : styles.selectLabel}>
            Select Template
          </InputLabel>
          <Select
            value={tempTemplateName}
            onChange={handleTemplateSelect}
            sx={styles.selectTemplate}
            variant="outlined">
            <MenuItem value="custom">Custom</MenuItem>
            {documentTemplates.map((template) => (
              <MenuItem
                key={template.template_name}
                value={template.template_name}>
                {template.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onConfirm} disabled={!docTitle.trim()}>
          {isEditMode ? 'Apply' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AIDocumentEditor = () => {
  const styles = useReportEditorStyles();
  const navigate = useNavigate();

  const { selectedBooking, documentTemplates } = useBookingsContext();

  const {
    appendNewDocument,
    documentsRef,
    documentSection,
    setBooking,
    saveEnabled,
    documentIndex,
    setDocumentSection,
    setDocumentIndex,
    setDocumentSections,
    setSaveEnabled,
  } = useAIDocumentEditorContext();

  const { fetchDocumentData } = useKnowledgeLibrary();
  const { formList } = useCopilotContext();

  const [leftPane, setLeftPane] = useState<number>(60);
  const [confirmModal, setConfirmModal] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isNewDocModalOpen, setIsNewDocModalOpen] = useState(false);
  const [newDocTitle, setNewDocTitle] = useState('');
  const [templateName, setTemplateName] = useState('custom');

  const handleExit = async () => {
    if (!saveEnabled) navigate('/');
    else setDialogOpen(true);
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (saveEnabled === false) {
        event.preventDefault();
      }
    };

    if (!selectedBooking) return navigate('/');
    setBooking({ ...selectedBooking });

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      console.log('::::unset booking...');
      setBooking(null);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const compareAndUpdateSection = () => {
    console.log(
      '::::inside compareAndUpdateSection, documentSection: ' + documentIndex,
      documentSection,
      documentsRef.current?.[documentIndex],
    );

    if (
      !documentSection?.index ||
      !documentsRef.current?.[documentIndex]?.sections?.[
        documentSection.index
      ] ||
      JSON.stringify(
        documentsRef.current[documentIndex].sections[documentSection.index],
      ) === JSON.stringify(documentSection)
    )
      return;

    setSaveEnabled(true);
    documentsRef.current[documentIndex].sections[documentSection.index] =
      documentSection;
  };

  const handleDocumentSelect = (event: SelectChangeEvent<string>) => {
    console.log('::::handleDocumentSelect...');
    if (!documentsRef.current) return;
    const value = event.target.value;

    compareAndUpdateSection();

    if (value === 'new') {
      setNewDocTitle('');
      setIsNewDocModalOpen(true);
      return;
    }

    const index = parseInt(value.replace('_', ''), 10);
    if (!documentsRef.current?.[index]) return;

    setDocumentSections(documentsRef.current[index].sections);
    console.log(
      '::::documentsRef.current[index].sections: ',
      documentsRef.current[index].sections[0],
    );
    console.log('::::setDocument section 6...');
    setDocumentSection(
      documentsRef.current[index].sections[0] || {
        index: 0,
        deleted: false,
        context: '',
        cards: [],
      },
    );
    setDocumentIndex(index);
  };

  const handleCreateDocument = () => {
    console.log(':::::handleCreateDocument, newDocTitle:', newDocTitle);
    if (!documentsRef.current || !newDocTitle.trim()) return;

    appendNewDocument(newDocTitle.trim());
    const documentIndex = documentsRef.current.length - 1;

    console.log(':::::handleCreateDocument, documentIndex:', documentIndex);

    setDocumentSections(documentsRef.current[documentIndex].sections);

    console.log(
      '::::documentsRef.current[documentIndex].sections[0]: ',
      documentsRef.current[documentIndex].sections[0],
    );
    console.log('::::setDocument section 7...');
    setDocumentSection(
      documentsRef.current[documentIndex].sections[0] || {
        index: 0,
        deleted: false,
        context: '',
        cards: [],
      },
    );
    setDocumentIndex(documentIndex);

    setIsNewDocModalOpen(false);
  };

  const [tempTemplateName, setTempTemplateName] = useState(templateName);

  const handleTemplateSelect = (event: SelectChangeEvent<string>) => {
    setTempTemplateName(event.target.value);
  };

  const handleEditDocument = (title?: string) => {
    if (!documentsRef.current?.[documentIndex]) return;
    setNewDocTitle(
      title !== undefined ? title : documentsRef.current[documentIndex].title,
    );
    setTemplateName(documentsRef.current[documentIndex].template_name);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!documentsRef.current?.[documentIndex] || !newDocTitle.trim()) return;

    const selectedTemplate = documentTemplates.find(
      (doc) => doc.template_name === tempTemplateName,
    );

    console.log(':::::selectedTemplate:', selectedTemplate);

    const document = documentsRef.current[documentIndex];

    document.title = newDocTitle.trim();
    document.template_name = tempTemplateName;

    if (selectedTemplate) {
      document.template_name = tempTemplateName;
      document.sections = deepClone(selectedTemplate.sections);

      for (const section of document.sections) {
        for (const card of section.cards) {
          console.log('::::card:', card);
          if (card.type !== 'Form') continue;

          const match = formList.find((f) => f.title === card.name);
          if (!match) {
            console.log('::::no match found, skipping...');
            continue;
          }

          try {
            console.log('::::found form:', match);
            const { data } = await fetchDocumentData(match.did);
            card.data = deepClone(data ?? []);
          } catch (err) {
            console.error('fetchDocumentData failed for', match.did, err);
          }
        }
      }

      console.log('::::setDocument section 8...');
      setDocumentSection(document.sections[0]);
      setDocumentSections(document.sections);
    }

    setSaveEnabled(true);
    setIsEditModalOpen(false);
    console.log(':::::documentIndex:', document);
  };

  useEffect(() => {
    console.log('::::documentsRef.current:', documentsRef.current);
    console.log('::::documentIndex:', documentIndex);
    console.log('::::document:', !documentsRef.current?.[documentIndex]?.title);

    if (
      documentsRef.current?.[documentIndex]?.title === 'New Document' &&
      documentSection?.cards.length === 0
    ) {
      handleEditDocument('');
      return;
    }
  }, [documentsRef.current]);

  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <Dialog open={dialogOpen}>
        <DialogTitle>Exit without saving changes?</DialogTitle>
        <DialogActions>
          <Button sx={{ mr: 'auto' }} onClick={() => setDialogOpen(false)}>
            Return
          </Button>
          <Button onClick={() => navigate('/')}>OK</Button>
        </DialogActions>
      </Dialog>

      <Box height="100vh">
        <Box sx={styles.header}>
          <Box display="flex">
            <Typography fontWeight={500} fontSize=".8125rem">
              HOMEOWNER:
              <Typography sx={styles.title} variant="caption">
                {selectedBooking?.title}
              </Typography>
            </Typography>

            <Box sx={styles.participant}></Box>
            <Box>
              <Box marginTop={'1rem'} mb=".75rem"></Box>
            </Box>
          </Box>

          <Box display={'flex'} alignItems={'center'} gap={2}>
            <FormControl
              sx={{ ...styles.selectContainer, flex: 1, maxWidth: '25rem' }}>
              <Select
                value={
                  documentsRef.current?.length ? `_${documentIndex}` : 'new'
                }
                onChange={handleDocumentSelect}
                sx={styles.select}
                variant="outlined">
                <MenuItem value="new">+ New</MenuItem>
                {(documentsRef.current ?? []).map((doc, index) => (
                  <MenuItem key={doc.template_name} value={`_${index}`}>
                    <DescriptionOutlinedIcon sx={{ fontSize: '1.2rem' }} />
                    {doc.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <IconButton
              onClick={() => handleEditDocument()}
              sx={{
                ...styles.downloadIcon,
                visibility: documentIndex >= 0 ? 'visible' : 'hidden',
              }}>
              <EditIcon />
            </IconButton>

            <CustomButton
              style={styles.exitButton}
              handleClick={handleExit}
              startIcon={
                <ExitToAppOutlined style={{ fontSize: '1.375rem' }} />
              }>
              Exit
            </CustomButton>
          </Box>
        </Box>

        <Box height={'calc(100vh - 3.125rem)'} display="flex">
          <Box
            id="_editor_canvas"
            sx={{ ...styles.roomPanel, width: `${leftPane}%` }}>
            <DocumentWorkspace />
          </Box>

          <SliderBar setWidth={setLeftPane} />
          <Box sx={styles.viewsPanel}>
            <Panels />
          </Box>
        </Box>
      </Box>

      <Modal open={confirmModal} onClose={() => setConfirmModal(false)}>
        <Box sx={styles.confirmModal}>
          <div
            style={{ padding: '20px', overflowY: 'auto', maxHeight: '80vh' }}>
            {documentSection?.cards.map((section, index) => {
              const promptCard = section as IPromptCard;
              const text = promptCard.modified
                ? new QuillDeltaToHtmlConverter(
                    (promptCard.modified as IDelta).ops,
                    {},
                  ).convert()
                : promptCard.response;

              return (
                <div key={index}>
                  <h2>{section.title}</h2>
                  <p>{text}</p>
                </div>
              );
            })}
          </div>
          <Box display={'flex'} justifyContent={'space-evenly'}>
            <Button onClick={() => setConfirmModal(false)}>
              <span style={{ textTransform: 'uppercase' }}>D</span>o not send
            </Button>
            <Button
            // sx={styles.sendReportButton}
            >
              Send the report
            </Button>
          </Box>
        </Box>
      </Modal>

      <DocumentDialog
        open={isNewDocModalOpen}
        mode="new"
        dialogTitle="New Document"
        onClose={() => setIsNewDocModalOpen(false)}
        onConfirm={handleCreateDocument}
        docTitle={newDocTitle}
        setNewDocTitle={setNewDocTitle}
        tempTemplateName={tempTemplateName}
        handleTemplateSelect={handleTemplateSelect}
        documentTemplates={documentTemplates}
        styles={styles}
      />

      <DocumentDialog
        open={isEditModalOpen}
        mode="edit"
        dialogTitle="Edit Document"
        onClose={() => setIsEditModalOpen(false)}
        onConfirm={handleSaveEdit}
        docTitle={newDocTitle}
        setNewDocTitle={setNewDocTitle}
        tempTemplateName={tempTemplateName}
        handleTemplateSelect={handleTemplateSelect}
        documentTemplates={documentTemplates}
        styles={styles}
      />
    </>
  );
};

export default AIDocumentEditor;
